import * as React from 'react'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { ActionButton, DialogFooter, IDialogProps, SearchBox } from '@fluentui/react'
import LegacyBaseDialog from '../LegacyBaseDialog'
import useEntity from '../../../hooks/useEntity'
import KvkSearchResultList, { KvkSearchResult } from '../../List/KvkSearchResult/KvkSearchResultList'
import Loading from '../../Loading'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { LegalStructure } from '../../../config/i18nConfig'
import useLegalStructures from '../../../hooks/useLegalStructures'
import { Destination } from '../../../context/AxiosContext'
import { LegalPersonMutationDto } from '../../Form/Party/LegalPersonForm'
import AuthorizationContext from '../../../context/AuthorizationContext'

type KvkSearchPageResult = {
    kvkNummer: string
    vestigingsnummer?: string
    naam: string
    adres?: KvkSearchAddress
    type: 'hoofdvestiging' | 'nevenvestiging' | 'rechtspersoon' | string
}

type KvkSearchAddress = {
    binnenlandsAdres: KvkSearchDutchAddress
    buitenlandsAdres: KvkSearchForeignAddress
}

type KvkSearchBaseAddress = {
    type: 'bezoekadres' | 'correspondentieadres'
}

type KvkSearchDutchAddress = KvkSearchBaseAddress & {
    straatnaam: string
    huisnummer: number
    huisletter: string
    postbusnummer: number
    postcode: string
    plaats: string
}

type KvkSearchForeignAddress = KvkSearchBaseAddress & {
    straatHuisnummer: string
    postcodeWoonplaats: string
    land: string
}

export type KvkSearchPage = {
    pagina: number
    aantal: number
    totaal: number
    resultaten: KvkSearchPageResult[]
}

type KvkLegalStructures =
    | 'Besloten Vennootschap'
    | 'Naamloze Vennootschap'
    | 'Eenmanszaak'
    | 'Stichting'
    | 'Vennootschap Onder Firma'
    | 'Maatschap'
    | 'Vereniging'
    | 'Commanditaire Vennootschap'
    | 'Coöperatie'
    | 'Publiekrechtelijke Rechtspersoon'

type KvkProfileAddress = {
    type: 'bezoekadres' | 'correspondentieadres'
    postcode: string
    plaats: string
    land: string
    postbusnummer?: string
    straatnaam?: string
    huisnummer?: string
    huisnummerToevoeging: string
}

type KvkProfilePage = {
    kvkNummer: string
    naam: string
    _embedded: {
        hoofdvestiging?: {
            adressen: KvkProfileAddress[]
        }
        eigenaar: {
            rechtsvorm: KvkLegalStructures
            adressen?: KvkProfileAddress[]
        }
    }
}

type KvkBranchProfilePage = {
    vestigingsnummer: string
    kvkNummer: string
    adressen?: KvkProfileAddress[]
}
export type KvkError = { code: string; omschrijving: string }

export type KvkErrorResponse = {
    fout: KvkError[]
}

export const isKvkError = (error: AxiosError): error is AxiosError<KvkErrorResponse> => !!(error.response?.data as KvkErrorResponse)?.fout

export type KvkSearchDialogProps = IDialogProps & {
    onSelectCompany: (company: Partial<LegalPersonMutationDto>) => void
}

// Rechtspersoon boven de vestigingen van hetzelfde KvK-nummer; een rechtspersoon-resultaat heeft geen adres.
const typeOrder: Record<string, number> = { rechtspersoon: 0, hoofdvestiging: 1, nevenvestiging: 2 }

const mapSearchResult = (page: KvkSearchPage): KvkSearchResult[] => {
    return page.resultaten
        .sort(
            (a: KvkSearchPageResult, b: KvkSearchPageResult) =>
                a.kvkNummer.localeCompare(b.kvkNummer) ||
                (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9) ||
                (a.vestigingsnummer ?? '').localeCompare(b.vestigingsnummer ?? ''),
        )
        .map<KvkSearchResult>(entry => ({
            number: entry.kvkNummer,
            branchNumber: entry.vestigingsnummer,
            type: entry.type,
            name: entry.naam,
            street: entry.adres?.binnenlandsAdres
                ? [entry.adres.binnenlandsAdres.straatnaam, entry.adres.binnenlandsAdres.huisletter, entry.adres.binnenlandsAdres.postbusnummer]
                      .filter(field => field)
                      .join(' ')
                : (entry.adres?.buitenlandsAdres?.straatHuisnummer ?? ''),
            locality: entry.adres?.binnenlandsAdres
                ? entry.adres.binnenlandsAdres.plaats
                : entry.adres?.buitenlandsAdres
                  ? `${entry.adres.buitenlandsAdres.postcodeWoonplaats} ${entry.adres.buitenlandsAdres.land}`
                  : '',
        }))
        .reduce(
            (acc: KvkSearchResult[], current: KvkSearchResult) =>
                acc.find(entry => entry.number === current.number && entry.branchNumber === current.branchNumber) ? acc : [...acc, current],
            [],
        )
}

const mapProfileResult = (getLegalStructureByName: (name: string) => LegalStructure, page: KvkProfilePage): Partial<LegalPersonMutationDto> => {
    const addresses = [...(page._embedded.hoofdvestiging?.adressen || []), ...(page._embedded.eigenaar?.adressen || [])]
    const address = addresses.find(address => address.type === 'bezoekadres')
    const poBoxAddress = addresses.find(address => address.type === 'correspondentieadres')

    return {
        cocNumber: page.kvkNummer,
        name: page.naam,
        legalStructure: getLegalStructureByName(page._embedded.eigenaar.rechtsvorm)?.alpha,
        addressStreet: address?.postbusnummer ? 'Postbus' : address?.straatnaam,
        addressNumber: address?.postbusnummer ?? `${address?.huisnummer ?? ''}${address?.huisnummerToevoeging ?? ''}`,
        addressPostalCode: address?.postcode,
        addressLocality: address?.plaats,
        addressCountry: 'NL',
        poBoxAddressStreet: poBoxAddress?.postbusnummer ? 'Postbus' : poBoxAddress?.straatnaam,
        poBoxAddressNumber: poBoxAddress?.postbusnummer ?? `${poBoxAddress?.huisnummer ?? ''}${poBoxAddress?.huisnummerToevoeging ?? ''}`,
        poBoxAddressPostalCode: poBoxAddress?.postcode,
        poBoxAddressLocality: poBoxAddress?.plaats,
        poBoxAddressCountry: poBoxAddress ? 'NL' : undefined,
    }
}

/**
 * Maps the branch profile (vestigingsprofiel) to overrides on top of the base profile:
 * the branch number and, when present, the addresses of the specific branch.
 */
const mapBranchProfileResult = (page: KvkBranchProfilePage): Partial<LegalPersonMutationDto> => {
    const address = page.adressen?.find(address => address.type === 'bezoekadres')
    const poBoxAddress = page.adressen?.find(address => address.type === 'correspondentieadres')

    return {
        cocBranchNumber: page.vestigingsnummer,
        ...(address
            ? {
                  addressStreet: address.postbusnummer ? 'Postbus' : address.straatnaam,
                  addressNumber: address.postbusnummer ?? `${address.huisnummer ?? ''}${address.huisnummerToevoeging ?? ''}`,
                  addressPostalCode: address.postcode,
                  addressLocality: address.plaats,
                  addressCountry: 'NL',
              }
            : {}),
        ...(poBoxAddress
            ? {
                  poBoxAddressStreet: poBoxAddress.postbusnummer ? 'Postbus' : poBoxAddress.straatnaam,
                  poBoxAddressNumber: poBoxAddress.postbusnummer ?? `${poBoxAddress.huisnummer ?? ''}${poBoxAddress.huisnummerToevoeging ?? ''}`,
                  poBoxAddressPostalCode: poBoxAddress.postcode,
                  poBoxAddressLocality: poBoxAddress.plaats,
                  poBoxAddressCountry: 'NL',
              }
            : {}),
    }
}

export default function KvkSearchDialog(props: KvkSearchDialogProps): ReactElement {
    const { host: currentHost } = useContext(AuthorizationContext)
    const { t: translate } = useTranslation()
    const { getLegalStructureByName } = useLegalStructures()

    const [query, setQuery] = useState<string>(null)
    const [selected, setSelected] = useState<KvkSearchResult>(null)
    const [items] = useEntity<KvkSearchResult[], KvkSearchPage, KvkErrorResponse>(
        query => ({
            url: `/coc/kvk/search/${currentHost?.RowKey}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
            params: { query },
        }),
        query !== '' ? query : null,
        result => mapSearchResult(result.data),
    )
    const [selectedItem] = useEntity<Partial<LegalPersonMutationDto>, KvkProfilePage, KvkErrorResponse>(
        number => ({
            url: `/coc/kvk/profile/${currentHost?.RowKey}/${number}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
        }),
        selected?.number ?? null,
        result => mapProfileResult(getLegalStructureByName, result.data),
    )
    const [selectedBranch] = useEntity<Partial<LegalPersonMutationDto>, KvkBranchProfilePage, KvkErrorResponse>(
        branchNumber => ({
            url: `/coc/kvk/branch/${currentHost?.RowKey}/${branchNumber}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
        }),
        selected?.branchNumber ?? null,
        result => mapBranchProfileResult(result.data),
    )
    const onDismiss = () => {
        setQuery(null)
        setSelected(null)
        props.onDismiss?.()
    }

    useEffect(() => {
        if (selectedItem.loaded && (!selected?.branchNumber || selectedBranch.loaded)) {
            // De naam van het aangeklikte zoekresultaat wint van de statutaire naam uit het
            // basisprofiel: bij een vestiging is dat de handelsnaam van die vestiging.
            props.onSelectCompany({ ...selectedItem.data, ...(selectedBranch.loaded ? { ...selectedBranch.data, name: selected.name } : {}) })
            onDismiss()
        }
    }, [selectedItem, selectedBranch])

    return (
        <LegacyBaseDialog {...props} dialogContentProps={{ title: translate('party.edit.coc.search.title') }} maxWidth={960} onDismiss={onDismiss}>
            {selectedItem.loading || selectedBranch.loading ? (
                <Loading />
            ) : (
                <>
                    <SearchBox underlined onSearch={setQuery} onClear={() => setQuery(null)} />
                    <KvkSearchResultList items={items} onSelect={setSelected} />
                </>
            )}
            <DialogFooter>
                <ActionButton text={translate('action.cancel')} onClick={onDismiss} />
            </DialogFooter>
        </LegacyBaseDialog>
    )
}
